export const EVENT_CREATOR_SOURCE = 'event_creator'
export const EVENT_CREATOR_ANALYTICS_SCHEMA_VERSION = 1

export const EVENT_CREATOR_EVENTS = {
    VIEWED: 'event_creator_viewed',
    EXPANDED: 'event_creator_expanded',
    COLLAPSED: 'event_creator_collapsed',
    CATEGORY_OPENED: 'event_creator_category_opened',
    STARTED: 'event_creator_started',
    TILE_SELECTED: 'event_creator_tile_selected',
    TILE_DESELECTED: 'event_creator_tile_deselected',
    SUBMISSION_BLOCKED: 'event_creator_submission_blocked',
    SUBMITTED: 'event_creator_submitted',
    ALTERNATIVE_RECOMMENDATIONS_REQUESTED: 'event_creator_alternative_recommendations_requested',
    RECOMMENDATIONS_VIEWED: 'event_creator_recommendations_viewed',
    RECOMMENDATIONS_FAILED: 'event_creator_recommendations_failed',
    EDIT_STARTED: 'event_creator_edit_started',
    SETUP_HELP_OPENED: 'event_creator_setup_help_opened',
    SETUP_VISIBILITY_CHANGED: 'event_creator_setup_visibility_changed',
    GALLERY_OPENED: 'event_creator_gallery_opened',
    TEMPLATE_OPENED: 'event_creator_template_opened',
    TEMPLATE_STARTED: 'event_creator_template_started',
}

const SESSION_STORAGE_KEY = 'event_creator_analytics_session_id'

const createRandomId = () => {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        const bytes = new Uint8Array(16)
        window.crypto.getRandomValues(bytes)
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export const createEventCreatorAnalyticsId = () => createRandomId()

export const getOrCreateEventCreatorSessionId = () => {
    if (typeof window === 'undefined') return createRandomId()

    try {
        const existingSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
        if (existingSessionId) return existingSessionId

        const sessionId = createRandomId()
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId)
        return sessionId
    } catch (error) {
        return createRandomId()
    }
}

export const createEventCreatorAnalyticsContext = ({
    language,
    surface,
    initialDisplayMode,
    onboardingInterestIds,
    onboardingInterestNames,
}) => ({
    analyticsSchemaVersion: EVENT_CREATOR_ANALYTICS_SCHEMA_VERSION,
    creatorSessionId: getOrCreateEventCreatorSessionId(),
    creatorInstanceId: createEventCreatorAnalyticsId(),
    environment: process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'unknown',
    language,
    origin: typeof window === 'undefined' ? '' : window.location.origin,
    path: typeof window === 'undefined' ? '' : window.location.pathname,
    surface,
    initialDisplayMode,
    onboardingInterestIds,
    onboardingInterestNames,
})

export const serializeEventCreatorAttribution = attribution => {
    if (!attribution) return {}

    return {
        source: EVENT_CREATOR_SOURCE,
        eventCreatorSessionId: attribution.creatorSessionId,
        eventCreatorInstanceId: attribution.creatorInstanceId,
        eventCreatorSubmissionId: attribution.submissionId,
        eventCreatorSubmissionNumber: attribution.submissionNumber,
        eventCreatorRecommendationIndex: attribution.recommendationIndex,
        eventCreatorRecommendationBatchNumber: attribution.recommendationBatchNumber,
        eventCreatorSelectedTemplateId: attribution.selectedTemplateId,
        eventCreatorExperimentVariant: attribution.experimentVariant,
        eventCreatorSurface: attribution.surface,
        eventCreatorInitialDisplayMode: attribution.initialDisplayMode,
        eventCreatorOnboardingInterestIds: attribution.onboardingInterestIds,
        eventCreatorOnboardingInterestNames: attribution.onboardingInterestNames,
    }
}

export const parseEventCreatorAttribution = queryParams => {
    if (
        queryParams.source !== EVENT_CREATOR_SOURCE ||
        !queryParams.eventCreatorSessionId ||
        !queryParams.eventCreatorInstanceId ||
        !queryParams.eventCreatorSubmissionId
    ) {
        return null
    }

    const submissionNumber = Number(queryParams.eventCreatorSubmissionNumber)
    const recommendationIndex = Number(queryParams.eventCreatorRecommendationIndex)
    const recommendationBatchNumber = Number(queryParams.eventCreatorRecommendationBatchNumber)
    const normalizeArrayParam = value => (Array.isArray(value) ? value : value ? [value] : [])

    return {
        source: EVENT_CREATOR_SOURCE,
        creatorSessionId: queryParams.eventCreatorSessionId,
        creatorInstanceId: queryParams.eventCreatorInstanceId,
        submissionId: queryParams.eventCreatorSubmissionId,
        ...(Number.isInteger(submissionNumber) && { submissionNumber }),
        ...(Number.isInteger(recommendationIndex) && { recommendationIndex }),
        ...(Number.isInteger(recommendationBatchNumber) && { recommendationBatchNumber }),
        ...(queryParams.eventCreatorSelectedTemplateId && {
            selectedTemplateId: queryParams.eventCreatorSelectedTemplateId,
        }),
        experimentVariant: queryParams.eventCreatorExperimentVariant,
        surface: queryParams.eventCreatorSurface,
        initialDisplayMode: queryParams.eventCreatorInitialDisplayMode,
        onboardingInterestIds: normalizeArrayParam(queryParams.eventCreatorOnboardingInterestIds),
        onboardingInterestNames: normalizeArrayParam(queryParams.eventCreatorOnboardingInterestNames),
    }
}
